/** Display helpers shared by the views. */

import { type ItemKind, OWN_KINDS, type OwnKind } from '@satisa-memory/shared'

/**
 * The memory's own entities, drawn as icons rather than a monogram
 * (SourceBadge.vue): the library's own kinds, and his typed notes' to-do and
 * decision, which only the ledger lists.
 */
export type EntityIcon = OwnKind | 'todo' | 'decision'

interface SourceMeta {
  label: string
  /** Monogram on the badge. */
  mark: string
  /** Badge colour; text on it is white, so keep it dark enough in both themes. */
  color: string
  /** Drawn instead of the monogram. */
  icon?: EntityIcon
}

const SOURCE_META: Record<string, SourceMeta> = {
  slack: { label: 'Slack', mark: 'S', color: '#611f69' },
  gdrive: { label: 'Google Drive', mark: 'D', color: '#1a5fd1' },
  claude_artifact: { label: 'Claude artifact', mark: 'C', color: '#c15f3c' },
  notion: { label: 'Notion', mark: 'N', color: '#3f3d38' },
  granola: { label: 'Granola', mark: 'G', color: '#4d7c0f' },
  gmail: { label: 'Gmail', mark: 'M', color: '#c5221f' },
  github: { label: 'GitHub', mark: 'H', color: '#24292f' },
  web: { label: 'Web', mark: 'W', color: '#52627a' },
  // Briefs, debriefs and the owner's notes; each kind has its own badge below.
  memory: { label: 'Memory', mark: 'M', color: '#7a5c12', icon: 'brief' },
  // Not a link source: a person invited to a meeting (People page).
  calendar: { label: 'Calendar', mark: 'C', color: '#1e6f5c' },
  calendar_only: { label: 'Calendar only', mark: 'C', color: '#1e6f5c' },
  // Not in SOURCES, but the sync records them; any other string falls back
  // to a grey monogram below.
  claude_share: { label: 'Claude chat', mark: 'C', color: '#9a4a2e' },
  figma: { label: 'Figma', mark: 'F', color: '#a33fd6' },
  // The URL names these two (worker lib/url-key.ts), whatever the sync says.
  ado: { label: 'Azure DevOps', mark: 'A', color: '#0b62b3' },
  linear: { label: 'Linear', mark: 'L', color: '#5e6ad2' },
  powerbi: { label: 'Power BI', mark: 'P', color: '#a8740a' },
  youtube: { label: 'YouTube', mark: 'Y', color: '#c4120c' },
  linkedin: { label: 'LinkedIn', mark: 'L', color: '#0a5a9c' },
  other: { label: 'Other', mark: '·', color: '#6b6560' }
}

/**
 * The memory's own links, by link kind: a brief (worker db/brief-store.ts), a
 * debrief (db/debriefs.ts, kind meeting_notes), and the owner's note behind a
 * to-do or a decision he typed (db/todos.ts, kind todo).
 */
const OWN_META: Record<EntityIcon, SourceMeta> = {
  brief: { label: 'Meeting brief', mark: 'B', color: '#7a5c12', icon: 'brief' },
  debrief: { label: 'Debrief', mark: 'D', color: '#8a4b14', icon: 'debrief' },
  todo: { label: 'To-do', mark: 'T', color: '#4338ca', icon: 'todo' },
  decision: { label: 'Decision', mark: 'D', color: '#0f6b5e', icon: 'decision' }
}
const OWN_PLURAL: Record<OwnKind, string> = {
  brief: 'Meeting briefs',
  debrief: 'Debriefs'
}

/**
 * The Library filters on one of the memory's own kinds as if it were a source,
 * `memory:<kind>` (library.ts sends it to the API as `own`), so the sidebar
 * lists them where the one "Memory" source would be.
 */
export const ownSource = (kind: OwnKind) => `memory:${kind}`

export function ownOf(source: string): OwnKind | null {
  const kind = source.startsWith('memory:') ? source.slice('memory:'.length) : ''
  return (OWN_KINDS as readonly string[]).includes(kind) ? (kind as OwnKind) : null
}

/**
 * A link's badge: its source, except the memory's own links, which say what
 * they are. `itemKind` tells a note's decision from its to-do (LinkSummary).
 */
export function sourceMeta(
  source: string,
  kind?: string | null,
  itemKind?: ItemKind | null
): SourceMeta {
  const own = ownOf(source)
  if (own) return { ...OWN_META[own], label: OWN_PLURAL[own] }
  if (source === 'memory') {
    if (kind === 'todo') return itemKind === 'decision' ? OWN_META.decision : OWN_META.todo
    if (kind === 'brief') return OWN_META.brief
    if (kind === 'meeting_notes') return OWN_META.debrief
  }
  return (
    SOURCE_META[source] ?? { label: source, mark: source.charAt(0).toUpperCase(), color: '#6b6560' }
  )
}

export function sourceLabel(
  source: string,
  kind?: string | null,
  itemKind?: ItemKind | null
): string {
  return sourceMeta(source, kind, itemKind).label
}

/**
 * List-row date: a time for today, day and month this year, and the full date
 * before that — the shortest form that is still unambiguous.
 */
export function compactDate(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Up to two initials for an avatar. */
export function initials(name: string): string {
  const parts = name
    .replace(/[<(].*$/, '')
    .split(/[\s._@-]+/)
    .filter(Boolean)
  const letters =
    parts.length > 1 ? parts[0]!.charAt(0) + parts.at(-1)!.charAt(0) : (parts[0] ?? '?').slice(0, 2)
  return letters.toUpperCase()
}

/** "shared_with" → "shared with". */
export function roleLabel(role: string): string {
  return role.replace(/_/g, ' ')
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** "Thu 23 Oct": a day close enough that its weekday is what matters. */
export function weekdayDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}
